import type React from 'react';

import { SearchRounded } from '@mui/icons-material';
import { Box, CircularProgress, InputAdornment, Stack, Typography } from '@mui/material';
import { FiqlSearch, ListPagination, defaultPerPage } from '@ragu-components/ord-components';
import { ListBase, RecordContextProvider, ResourceContextProvider, useListContext, useTranslate } from 'react-admin';

import type { RaRecord, SortPayload, FilterPayload } from 'react-admin';

/** Keeps the search field from stretching to the full container width */
const searchMaxWidth = 320;

export interface ResourceChipListProps {
  /** The resource to fetch */
  resource: string;
  /** Filter to apply to the list */
  filter: FilterPayload;
  /** Maximum number of items to show */
  perPage?: number;
  /** Sort configuration */
  sort?: SortPayload;
  /** Translation key or label when list is empty */
  emptyLabel?: string;
  /** Action button element (e.g., for adding new items) */
  actionButton?: React.ReactNode;
  /** Render function for each record - receives record and renders a chip */
  children: (record: RaRecord) => React.ReactNode;
}

interface ChipListContentProps {
  resource: string;
  emptyLabel: string;
  actionButton?: React.ReactNode;
  children: (record: RaRecord) => React.ReactNode;
}

const ChipListContent = ({
  resource,
  emptyLabel,
  actionButton,
  children,
}: ChipListContentProps): React.ReactElement => {
  const translate = useTranslate();
  const { data, isLoading } = useListContext<RaRecord>();

  return (
    <Stack spacing={2} sx={{ width: '100%', flex: 1, minHeight: 0 }}>
      {actionButton !== undefined && <Box sx={{ display: 'flex' }}>{actionButton}</Box>}

      <FiqlSearch
        hasOwnFormContext={false}
        resource={resource}
        type='fixed'
        autoFocus={false}
        sx={{ width: '100%', maxWidth: searchMaxWidth }}
        persistInUrl={false}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRounded />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && (data === undefined || data.length === 0) && (
          <Typography variant='body2'>{translate(emptyLabel)}</Typography>
        )}

        {!isLoading && data !== undefined && data.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.map((record) => (
              <RecordContextProvider key={record.id} value={record}>
                {children(record)}
              </RecordContextProvider>
            ))}
          </Box>
        )}
      </Box>

      <ListPagination persistInUrl={false} />
    </Stack>
  );
};

/**
 * A container component for displaying records as chips.
 * Handles listing, search, and pagination.
 * Each record is rendered by the children render function.
 */
export const ResourceChipList = ({
  resource,
  filter,
  perPage = defaultPerPage,
  sort = { field: 'id', order: 'ASC' },
  emptyLabel = 'ra.navigation.no_results',
  actionButton,
  children,
}: ResourceChipListProps): React.ReactElement => {
  return (
    <ResourceContextProvider value={resource}>
      <ListBase disableSyncWithLocation filter={filter} perPage={perPage} sort={sort}>
        <ChipListContent resource={resource} emptyLabel={emptyLabel} actionButton={actionButton}>
          {children}
        </ChipListContent>
      </ListBase>
    </ResourceContextProvider>
  );
};

export default ResourceChipList;
